const BASE_HREF_COOKIE_KEY = 'BASE_HREF';

type BaseHrefCookie = {
  entidade?: number | string;
  mes?: number;
  ano?: number;
};

const getCookie = (key: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  return document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith(`${key}=`))
    ?.substring(key.length + 1);
};

const setCookie = (key: string, value: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const maxAge = 7 * 24 * 60 * 60;
  const hostname = window.location.hostname;
  const encodedValue = encodeURIComponent(value);

  if (hostname) {
    document.cookie = `${key}=${encodedValue}; path=/; max-age=${maxAge}; domain=${hostname}`;
  }
};

export const getBaseHrefCookie = (): BaseHrefCookie | undefined => {
  const cookie = getCookie(BASE_HREF_COOKIE_KEY);

  if (!cookie) {
    return undefined;
  }

  try {
    return JSON.parse(decodeURIComponent(cookie));
  } catch (error) {
    return undefined;
  }
};

export const setBaseHrefEntidadeCookie = (
  entidadeId: number | string
): void => {
  const currentCookie = getBaseHrefCookie();
  const currentDate = new Date();
  const baseHref: BaseHrefCookie = {
    entidade: entidadeId,
    mes: currentCookie?.mes ?? currentDate.getMonth() + 1,
    ano: currentCookie?.ano ?? currentDate.getFullYear()
  };

  setCookie(BASE_HREF_COOKIE_KEY, JSON.stringify(baseHref));
};

export const clearBaseHrefCookie = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const hostname = window.location.hostname;

  if (hostname) {
    document.cookie = `${BASE_HREF_COOKIE_KEY}=; path=/; max-age=0; domain=${hostname}`;
  }
};

export const isEntidadeIdNumerico = (
  value: number | string | null | undefined
): value is number | string =>
  value !== undefined && value !== null && /^\d+$/.test(value.toString());

export const isEntidadeIdValido = (
  value: number | string | null | undefined
): value is number | string => isEntidadeIdNumerico(value);

export const getEntidadeIdNumericoFromStorage = (
  storageKey: string = 'entidade'
): string | null => {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const value = localStorage.getItem(storageKey);

  if (value && !isEntidadeIdValido(value)) {
    localStorage.removeItem(storageKey);
    return null;
  }

  return value;
};

export const syncLocalStorageEntidadeFromBaseHref = (
  storageKey: string = 'entidade'
): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const entidadeStorage = getEntidadeIdNumericoFromStorage(storageKey);
  const entidadeCookie = getBaseHrefCookie()?.entidade;

  if (
    !entidadeStorage &&
    isEntidadeIdValido(entidadeCookie) &&
    entidadeCookie.toString() !== '0'
  ) {
    localStorage.setItem(storageKey, entidadeCookie.toString());
  }
};
